import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const MAX_NOME = 150;

interface Props {
  userId: string;
  nomeAtual: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (novoNome: string) => void;
}

export const EditarNomeModal = ({ userId, nomeAtual, open, onClose, onSuccess }: Props) => {
  const [nome, setNome] = useState(nomeAtual);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setNome(nomeAtual);
  }, [open, nomeAtual]);

  const handleSalvar = async () => {
    const trimmed = nome.trim();
    if (!trimmed) {
      toast.error("Nome não pode ser vazio");
      return;
    }
    if (trimmed.length > MAX_NOME) {
      toast.error(`Nome deve ter no máximo ${MAX_NOME} caracteres`);
      return;
    }
    setSaving(true);
    try {
      await api.put(`/colaboradores/${userId}/nome`, { name: trimmed });
      toast.success("Nome atualizado com sucesso");
      onSuccess(trimmed);
      onClose();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        toast.error("Você não tem permissão para editar este usuário");
      } else if (err instanceof ApiError && err.status === 404) {
        toast.error("Usuário não encontrado");
      } else {
        toast.error(err instanceof Error ? err.message : "Erro ao atualizar nome");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Editar nome</DialogTitle>
          <DialogDescription>Informe o novo nome completo.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="novo-nome">Nome completo</Label>
          <Input
            id="novo-nome"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            maxLength={MAX_NOME}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancelar</Button>
          <Button onClick={handleSalvar} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
